import React, { useState, useEffect, useRef } from 'react';
import { FileText, Users, DollarSign, Plus, Edit, Trash2, Search, Printer, Send, Eye, BarChart3, X, Square, CheckSquare, Trash, Package, Download, History, ArrowUpDown, AlertTriangle, ArrowLeft, Tag } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import StatisticsPanel from './StatisticsPanel';
import { ClientDetailDialog } from './ClientsPanel';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const BillingPanel = () => {
  const [activeSubTab, setActiveSubTab] = useState('invoices');
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [clientDetailId, setClientDetailId] = useState(null);
  // Selección múltiple para cambio de precio en lote
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [showBulkPriceModal, setShowBulkPriceModal] = useState(false);
  const [bulkPriceInput, setBulkPriceInput] = useState('');
  const [showClientDialog, setShowClientDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showInvoiceView, setShowInvoiceView] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [products, setProducts] = useState([]);
  const [siteConfig, setSiteConfig] = useState(null);
  
  // Transactions state
  const [transactions, setTransactions] = useState([]);
  const [transactionFilter, setTransactionFilter] = useState('all');
  const [editingPayment, setEditingPayment] = useState(null);
  const [showEditPaymentDialog, setShowEditPaymentDialog] = useState(false);
  const [paymentEditForm, setPaymentEditForm] = useState({ amount: 0, payment_method: 'efectivo', notes: '' });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  
  // Estado para confirmar anulación/eliminación de factura
  const [showInvoiceDeleteConfirm, setShowInvoiceDeleteConfirm] = useState(null);
  const [invoiceDeleteAction, setInvoiceDeleteAction] = useState('cancel'); // 'cancel' o 'delete'
  
  // Estado de reconciliación
  const [reconciling, setReconciling] = useState(false);
  
  // Filter state for invoices
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('all');
  
  // Selection state for bulk actions
  const [selectedInvoices, setSelectedInvoices] = useState(new Set());
  
  // Product search state
  const [productSearch, setProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const productSearchRef = useRef(null);
  const [productCategoryFilter, setProductCategoryFilter] = useState('all');
  
  // Visual product picker (modal grid)
  const [showVisualPicker, setShowVisualPicker] = useState(false);
  const [visualPickerSearch, setVisualPickerSearch] = useState('');
  const [visualPickerCategory, setVisualPickerCategory] = useState('all');
  
  // Estado para zoom de imagen de producto
  const [zoomImage, setZoomImage] = useState(null);
  
  // Referencia para captura de imagen de factura
  const invoiceViewRef = useRef(null);
  const [savingImage, setSavingImage] = useState(false);
  
  // Invoice form state
  const [invoiceForm, setInvoiceForm] = useState({
    client_id: '',
    invoice_type: 'contado',
    display_mode: 'category', // Por defecto por categoría
    items: [],
    discount: 0,
    tax: 0,
    notes: '',
    initial_payment: 0,
    payment_method: 'efectivo'
  });

  // Client form state
  const [clientForm, setClientForm] = useState({
    name: '', phone: '', address: '', document: '', email: '', notes: ''
  });

  // Payment form state
  const [paymentForm, setPaymentForm] = useState({
    client_id: '', amount: 0, payment_method: 'efectivo', notes: ''
  });

  // Cargar configuración del sitio
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const res = await api.get('/config');
        setSiteConfig(res.data);
      } catch (error) {
        console.error('Error loading site config:', error);
      }
    };
    loadConfig();
  }, []);

  useEffect(() => {
    fetchData();
  }, [activeSubTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [invoicesRes, clientsRes, summaryRes, transactionsRes] = await Promise.all([
        api.get('/invoices', { headers }),
        api.get('/clients', { headers }),
        api.get('/billing/summary', { headers }),
        api.get('/transactions', { headers })
      ]);

      setInvoices(invoicesRes.data);
      setClients(clientsRes.data);
      setSummary(summaryRes.data);
      setTransactions(transactionsRes.data);
    } catch (error) {
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  // Funciones para editar y eliminar pagos
  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setPaymentEditForm({
      amount: payment.amount || 0,
      payment_method: payment.payment_method || payment.data?.payment_method || 'efectivo',
      notes: payment.notes || payment.data?.notes || ''
    });
    setShowEditPaymentDialog(true);
  };

  const handleSavePaymentEdit = async () => {
    if (!editingPayment) return;
    
    try {
      const token = localStorage.getItem('token');
      await api.put(`/payments/${editingPayment.id}`, paymentEditForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pago actualizado correctamente');
      setShowEditPaymentDialog(false);
      setEditingPayment(null);
      fetchData();
    } catch (error) {
      toast.error('Error actualizando pago');
      console.error(error);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/payments/${paymentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pago eliminado y revertido correctamente');
      setShowDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error('Error eliminando pago');
      console.error(error);
    }
  };

  const handleReconcile = async () => {
    if (reconciling) return;
    const confirmMsg = 'Esta acción reconstruirá el historial de pagos de todas las facturas a partir de los pagos vivos y recalculará los saldos de los clientes. ¿Continuar?';
    if (!window.confirm(confirmMsg)) return;
    setReconciling(true);
    try {
      const token = localStorage.getItem('token');
      const res = await api.post('/billing/reconcile', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Sincronizado: ${res.data.invoices_fixed} facturas, ${res.data.clients_fixed} clientes`);
      fetchData();
    } catch (error) {
      toast.error('Error en la reconciliación');
      console.error(error);
    } finally {
      setReconciling(false);
    }
  };

  const filteredTransactions = transactions.filter(t => {
    if (transactionFilter === 'all') return true;
    return t.type === transactionFilter;
  });

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/inventory/products', { headers: { Authorization: `Bearer ${token}` } });
      // Mostrar TODOS los productos, no solo los que tienen stock
      setProducts(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleCreateInvoice = async () => {
    // Si estamos editando, usar la función de update
    if (editingInvoice) {
      await handleUpdateInvoice();
      return;
    }
    
    if (!invoiceForm.client_id || invoiceForm.items.length === 0) {
      toast.error('Selecciona un cliente y al menos un producto');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await api.post('/invoices', invoiceForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Factura creada');
      setShowInvoiceDialog(false);
      resetInvoiceForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear factura');
    }
  };

  const handleCreateClient = async () => {
    if (!clientForm.name) {
      toast.error('El nombre es obligatorio');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (editingClient) {
        await api.put(`/clients/${editingClient.id}`, clientForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Cliente actualizado');
      } else {
        await api.post('/clients', clientForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Cliente creado');
      }
      setShowClientDialog(false);
      setEditingClient(null);
      setClientForm({ name: '', phone: '', address: '', document: '', email: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error('Error guardando cliente');
    }
  };

  const handleCreatePayment = async () => {
    if (!paymentForm.client_id || paymentForm.amount <= 0) {
      toast.error('Selecciona un cliente y monto válido');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const result = await api.post('/payments', paymentForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Pago registrado');
      setShowPaymentDialog(false);
      setPaymentForm({ client_id: '', amount: 0, payment_method: 'efectivo', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar pago');
    }
  };

  const handleCancelInvoice = async (invoiceId) => {
    try {
      const token = localStorage.getItem('token');
      await api.post(`/invoices/${invoiceId}/cancel`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Factura anulada correctamente');
      setShowInvoiceDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error('Error al anular factura');
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/invoices/${invoiceId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Factura eliminada definitivamente');
      setShowInvoiceDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar factura');
    }
  };

  // NUEVO: Función para iniciar edición de factura
  const startEditInvoice = async (invoice) => {
    if (invoice.status === 'anulada') {
      toast.error('No se puede editar una factura anulada');
      return;
    }
    
    // Cargar productos disponibles
    await fetchProducts();
    
    // Cargar los items con stock disponible
    const itemsForEdit = (invoice.items_detail || invoice.items || []).map(item => ({
      product_id: item.product_id,
      product_name: item.product_name,
      quantity: item.quantity,
      unit_price: item.unit_price,
      original_quantity: item.quantity, // Guardar cantidad original para calcular diferencia
      max_stock: item.quantity + 50 // Permitir aumentar stock (se validará en backend)
    }));
    
    setEditingInvoice(invoice);
    setInvoiceForm({
      client_id: invoice.client_id,
      invoice_type: invoice.invoice_type || 'contado',
      display_mode: invoice.display_mode || 'product',
      items: itemsForEdit,
      discount: invoice.discount || 0,
      tax: invoice.tax || 0,
      notes: invoice.notes || '',
      initial_payment: 0,
      payment_method: 'efectivo'
    });
    setShowInvoiceDialog(true);
  };

  // NUEVO: Función para guardar factura editada
  const handleUpdateInvoice = async () => {
    if (!editingInvoice) return;
    
    if (invoiceForm.items.length === 0) {
      toast.error('La factura debe tener al menos un producto');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      const updateData = {
        client_id: invoiceForm.client_id,
        invoice_type: invoiceForm.invoice_type,
        display_mode: invoiceForm.display_mode,
        items: invoiceForm.items.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          unit_price: item.unit_price
        })),
        discount: invoiceForm.discount,
        tax: invoiceForm.tax,
        notes: invoiceForm.notes
      };
      
      await api.put(`/invoices/${editingInvoice.id}`, updateData, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      
      toast.success('Factura actualizada correctamente');
      setShowInvoiceDialog(false);
      setEditingInvoice(null);
      resetInvoiceForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar factura');
    }
  };

  // NUEVO: Función para actualizar precio de un item
  const updateItemPrice = (productId, newPrice) => {
    const newItems = invoiceForm.items.map(item => 
      item.product_id === productId 
        ? { ...item, unit_price: parseFloat(newPrice) || 0 }
        : item
    );
    setInvoiceForm({ ...invoiceForm, items: newItems });
  };

  // Función para resetear formulario
  const resetInvoiceForm = () => {
    setInvoiceForm({
      client_id: '', 
      invoice_type: 'contado', 
      display_mode: 'product', 
      items: [], 
      discount: 0, 
      tax: 0, 
      notes: '', 
      initial_payment: 0, 
      payment_method: 'efectivo'
    });
    setSelectedItemIds([]);
  };

  // Cerrar modal factura reseteando estado (usado por Volver, X y click fuera)
  const closeInvoiceDialog = () => {
    setShowInvoiceDialog(false);
    setEditingInvoice(null);
    resetInvoiceForm();
  };

  const getBalanceColor = (balance) => {
    if (balance > 0) return 'text-green-400';
    if (balance < 0) return 'text-red-400';
    return 'text-blue-400';
  };

  const getStatusBadge = (status) => {
    const styles = {
      'pendiente': 'bg-yellow-500/20 text-yellow-400',
      'parcial': 'bg-orange-500/20 text-orange-400',
      'pagada': 'bg-green-500/20 text-green-400',
      'anulada': 'bg-red-500/20 text-red-400'
    };
    return styles[status] || 'bg-white/10 text-white/60';
  };

  // Filtrar facturas por estado
  const filteredInvoices = invoiceStatusFilter === 'all' 
    ? invoices 
    : invoices.filter(inv => inv.status === invoiceStatusFilter);

  // Calcular totales de facturas filtradas
  const filteredTotals = {
    total: filteredInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0),
    paid: filteredInvoices.reduce((sum, inv) => sum + (inv.amount_paid || 0), 0),
    balance: filteredInvoices.reduce((sum, inv) => sum + (inv.balance_due || 0), 0),
    count: filteredInvoices.length
  };

  // Selection handlers for bulk actions
  const toggleInvoiceSelection = (invoiceId) => {
    const newSelection = new Set(selectedInvoices);
    if (newSelection.has(invoiceId)) {
      newSelection.delete(invoiceId);
    } else {
      newSelection.add(invoiceId);
    }
    setSelectedInvoices(newSelection);
  };

  const selectAllInvoices = () => {
    const allIds = new Set(invoices.map(inv => inv.id));
    setSelectedInvoices(allIds);
  };

  const clearInvoiceSelection = () => {
    setSelectedInvoices(new Set());
  };

  const selectCancelledInvoices = () => {
    const cancelledIds = new Set(invoices.filter(inv => inv.status === 'anulada').map(inv => inv.id));
    setSelectedInvoices(cancelledIds);
  };

  // Bulk delete cancelled invoices
  const handleBulkDeleteCancelled = async () => {
    const cancelledToDelete = [...selectedInvoices].filter(id => {
      const inv = invoices.find(i => i.id === id);
      return inv && inv.status === 'anulada';
    });

    if (cancelledToDelete.length === 0) {
      toast.error('Solo se pueden eliminar facturas anuladas');
      return;
    }

    if (!window.confirm(`¿Está seguro de eliminar ${cancelledToDelete.length} factura(s) anulada(s)? Esta acción no se puede deshacer.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      let deleted = 0;
      
      for (const id of cancelledToDelete) {
        try {
          await api.delete(`/invoices/${id}`, { headers: { Authorization: `Bearer ${token}` } });
          deleted++;
        } catch (err) {
          console.error(`Error deleting invoice ${id}:`, err);
        }
      }
      
      toast.success(`${deleted} factura(s) eliminada(s) correctamente`);
      setSelectedInvoices(new Set());
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar facturas');
    }
  };

  const addItemToInvoice = (product) => {
    const existingIndex = invoiceForm.items.findIndex(i => i.product_id === product.id);
    
    // Calcular stock total de todas las bodegas
    const inventory = product.inventory || { china: 0, contenedor: 0, stock: 0, sin_stock: 0, archivados: 0 };
    const totalInventory = (inventory.china || 0) + (inventory.contenedor || 0) + (inventory.stock || 0) + (product.stock || 0);
    const maxStock = Math.max(totalInventory, 999); // Mínimo 999 para permitir facturar
    
    if (existingIndex >= 0) {
      const newItems = [...invoiceForm.items];
      newItems[existingIndex].quantity += 1;
      setInvoiceForm({ ...invoiceForm, items: newItems });
    } else {
      setInvoiceForm({
        ...invoiceForm,
        items: [...invoiceForm.items, {
          product_id: product.id,
          product_name: product.name,
          product_image: product.images?.[0] || null,
          category: product.category,
          quantity: 1,
          unit_price: product.price,
          max_stock: maxStock
        }]
      });
    }
  };

  const calculateInvoiceTotal = () => {
    const subtotal = invoiceForm.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    return subtotal - invoiceForm.discount + invoiceForm.tax;
  };

  const printInvoice = (invoice) => {
    const isGrouped = invoice.display_mode === 'category' && invoice.items_grouped && invoice.items_grouped.length > 0;
    const itemsToShow = isGrouped ? invoice.items_grouped : (invoice.items_detail || invoice.items || []);
    
    let itemsHtml = '';
    if (isGrouped) {
      itemsHtml = `
        <table>
          <thead>
            <tr>
              <th>Categoría</th>
              <th>Cantidad</th>
              <th>Subtotal</th>
            </tr>
          </thead>
          <tbody>
            ${itemsToShow.map(group => `
              <tr>
                <td>
                  <strong>${group.category_display}</strong>
                  ${group.products_detail ? `<br><small style="color: #666;">${group.products_detail.map(p => `${p.name} x${p.quantity}`).join(', ')}</small>` : ''}
                </td>
                <td style="text-align:center;">${group.quantity}</td>
                <td style="text-align:right;">$${group.subtotal.toLocaleString()}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `;
    } else {
      itemsHtml = `
        <table>
          <thead>
            <tr>
              <th>Producto</th>
              <th>Cantidad</th>
              <th>Precio Unit.</th>
              <th>Subtotal</th>
            </tr>
          </thead>
          <tbody>
            ${itemsToShow.map(item => `
              <tr>
                <td>${item.product_name}</td>
                <td style="text-align:center;">${item.quantity}</td>
                <td style="text-align:right;">$${item.unit_price?.toLocaleString()}</td>
                <td style="text-align:right;">$${item.subtotal?.toLocaleString()}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `;
    }
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Factura #${invoice.invoice_number}</title>
        <style>
          body { font-family: 'Helvetica Neue', Arial, sans-serif; padding: 40px; color: #333; max-width: 800px; margin: 0 auto; }
          .header { text-align: center; margin-bottom: 40px; border-bottom: 2px solid #000; padding-bottom: 20px; }
          .header h1 { font-size: 28px; margin: 0; letter-spacing: 4px; }
          .header p { margin: 5px 0; color: #666; }
          .invoice-info { display: flex; justify-content: space-between; margin-bottom: 30px; }
          .invoice-info div { flex: 1; }
          .invoice-info h3 { font-size: 12px; text-transform: uppercase; color: #666; margin-bottom: 8px; }
          .invoice-info p { margin: 4px 0; font-size: 14px; }
          table { width: 100%; border-collapse: collapse; margin: 30px 0; }
          th { background: #000; color: #fff; padding: 12px; text-align: left; font-size: 11px; text-transform: uppercase; }
          td { padding: 12px; border-bottom: 1px solid #eee; font-size: 14px; }
          .totals { text-align: right; margin-top: 20px; }
          .totals p { margin: 8px 0; font-size: 14px; }
          .totals .total { font-size: 20px; font-weight: bold; }
          .payments { margin-top: 30px; padding: 20px; background: #f9f9f9; }
          .payments h3 { font-size: 12px; text-transform: uppercase; margin-bottom: 10px; }
          .footer { margin-top: 50px; text-align: center; font-size: 12px; color: #999; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>HAUTE</h1>
          <p>HAUTE · Distribuidor Mayorista de Moda Premium</p>
        </div>
        
        <div class="invoice-info">
          <div>
            <h3>Factura</h3>
            <p><strong>#${invoice.invoice_number}</strong></p>
            <p>Fecha: ${new Date(invoice.created_at).toLocaleDateString('es-CO')}</p>
            <p>Tipo: ${invoice.invoice_type.toUpperCase()}</p>
            ${isGrouped ? '<p><em>Vista resumida por categoría</em></p>' : ''}
          </div>
          <div>
            <h3>Cliente</h3>
            <p><strong>${invoice.client_name}</strong></p>
            ${invoice.client_document ? `<p>Doc: ${invoice.client_document}</p>` : ''}
            ${invoice.client_phone ? `<p>Tel: ${invoice.client_phone}</p>` : ''}
            ${invoice.client_address ? `<p>${invoice.client_address}</p>` : ''}
          </div>
        </div>
        
        ${itemsHtml}
        
        <div class="totals">
          <p>Subtotal: $${invoice.subtotal.toLocaleString()}</p>
          ${invoice.discount > 0 ? `<p>Descuento: -$${invoice.discount.toLocaleString()}</p>` : ''}
          ${invoice.tax > 0 ? `<p>Impuesto: $${invoice.tax.toLocaleString()}</p>` : ''}
          <p class="total">TOTAL: $${invoice.total.toLocaleString()}</p>
          <p style="color: green;">Pagado: $${invoice.amount_paid.toLocaleString()}</p>
          <p style="color: ${invoice.balance_due > 0 ? 'red' : 'green'};">Saldo: $${invoice.balance_due.toLocaleString()}</p>
        </div>
        
        ${invoice.payments && invoice.payments.length > 0 ? `
          <div class="payments">
            <h3>Historial de Pagos</h3>
            ${invoice.payments.map(p => `
              <p>${new Date(p.created_at).toLocaleDateString('es-CO')} - $${p.amount.toLocaleString()} (${p.payment_method})</p>
            `).join('')}
          </div>
        ` : ''}
        
        ${invoice.notes ? `<p style="margin-top: 20px; font-style: italic;">Notas: ${invoice.notes}</p>` : ''}
        
        <div class="footer">
          <p>Gracias por su compra</p>
        </div>
        
        <script>window.print();</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const shareWhatsApp = (invoice, clientPhone = null) => {
    const isGrouped = invoice.display_mode === 'category' && invoice.items_grouped && invoice.items_grouped.length > 0;
    const itemsToShow = isGrouped ? invoice.items_grouped : (invoice.items_detail || invoice.items || []);
    
    let itemsText = '';
    if (isGrouped) {
      itemsText = itemsToShow.map(g => `• ${g.category_display} x${g.quantity} = $${g.subtotal.toLocaleString()}`).join('%0A');
    } else {
      itemsText = itemsToShow.map(i => `• ${i.product_name} x${i.quantity} = $${i.subtotal?.toLocaleString()}`).join('%0A');
    }
    
    const text = `*FACTURA #${invoice.invoice_number}*%0A` +
      `Fecha: ${new Date(invoice.created_at).toLocaleDateString('es-CO')}%0A` +
      `Cliente: ${invoice.client_name}%0A%0A` +
      `*Detalle:*%0A` +
      itemsText +
      `%0A%0A*TOTAL: $${invoice.total.toLocaleString()}*%0A` +
      `Pagado: $${invoice.amount_paid.toLocaleString()}%0A` +
      `Saldo: $${invoice.balance_due.toLocaleString()}%0A%0A` +
      `_HAUTE · Distribuidor Mayorista de Moda Premium_`;
    
    // Usar el teléfono del cliente si está disponible, o el número configurado
    const phoneToUse = clientPhone || invoice.client_phone || siteConfig?.whatsapp_number || '';
    const cleanPhone = phoneToUse.replace(/[^0-9]/g, ''); // Limpiar el número
    
    const url = cleanPhone ? `https://wa.me/${cleanPhone}?text=${text}` : `https://wa.me/?text=${text}`;
    window.open(url, '_blank');
  };

  // Función para generar PDF (auto multi-página para acumuladas largas)
  const generateInvoicePDF = async (invoice, { download = true } = {}) => {
    if (!invoiceViewRef.current) return null;

    const buttonsContainer = invoiceViewRef.current.querySelector('.invoice-actions-container');
    if (buttonsContainer) buttonsContainer.style.display = 'none';

    try {
      const canvas = await html2canvas(invoiceViewRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true,
        windowWidth: invoiceViewRef.current.scrollWidth,
        windowHeight: invoiceViewRef.current.scrollHeight,
      });

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();   // 210mm
      const pageHeight = pdf.internal.pageSize.getHeight(); // 297mm
      const margin = 8;
      const usableWidth = pageWidth - margin * 2;

      const imgWidthPx = canvas.width;
      const imgHeightPx = canvas.height;
      const pxPerMm = imgWidthPx / usableWidth;
      const usableHeightPx = (pageHeight - margin * 2) * pxPerMm;

      const imgData = canvas.toDataURL('image/png');

      // Recorte por páginas
      let renderedHeight = 0;
      let page = 0;
      while (renderedHeight < imgHeightPx) {
        const sliceHeight = Math.min(usableHeightPx, imgHeightPx - renderedHeight);
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = imgWidthPx;
        sliceCanvas.height = sliceHeight;
        const ctx = sliceCanvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, imgWidthPx, sliceHeight);
        const img = new Image();
        img.src = imgData;
        await new Promise((res) => { img.onload = res; });
        ctx.drawImage(img, 0, -renderedHeight);
        const sliceData = sliceCanvas.toDataURL('image/png');
        const sliceHeightMm = sliceHeight / pxPerMm;

        if (page > 0) pdf.addPage();
        pdf.addImage(sliceData, 'PNG', margin, margin, usableWidth, sliceHeightMm);
        // Pie de página
        pdf.setFontSize(8);
        pdf.setTextColor(150);
        pdf.text(`HAUTE · Página ${page + 1}`, pageWidth / 2, pageHeight - 4, { align: 'center' });

        renderedHeight += sliceHeight;
        page += 1;
      }

      const filename = `Factura_${invoice.invoice_number || invoice.id.slice(0,8)}.pdf`;
      if (download) {
        pdf.save(filename);
        toast.success('PDF descargado');
      }
      return pdf;
    } catch (error) {
      console.error('Error generando PDF:', error);
      toast.error('Error al generar PDF');
      return null;
    } finally {
      if (buttonsContainer) buttonsContainer.style.display = 'flex';
    }
  };

  const sendInvoiceWhatsApp = async (invoice) => {
    // 1) Generar y descargar PDF automáticamente
    const pdf = await generateInvoicePDF(invoice, { download: true });
    if (!pdf) return;

    // 2) Abrir WhatsApp con mensaje preparado para adjuntar el PDF descargado
    const phoneToUse = invoice.client_phone || siteConfig?.whatsapp_number || '';
    const cleanPhone = phoneToUse.replace(/[^0-9]/g, '');
    const text = encodeURIComponent(
      `Hola ${invoice.client_name},\n\n` +
      `Te comparto tu factura #${invoice.invoice_number || invoice.id.slice(0,8)} en PDF.\n\n` +
      `Total: $${(invoice.total || 0).toLocaleString('es-CO')}\n` +
      `Saldo: $${(invoice.balance_due || 0).toLocaleString('es-CO')}\n\n` +
      `HAUTE · Distribuidor Mayorista de Moda Premium`
    );
    const url = cleanPhone ? `https://wa.me/${cleanPhone}?text=${text}` : `https://wa.me/?text=${text}`;
    toast.info('PDF descargado. Adjúntalo en WhatsApp que se abrió.');
    window.open(url, '_blank');
  };

  // Función para guardar factura como imagen
  const saveInvoiceAsImage = async (invoice) => {
    if (!invoiceViewRef.current) return;
    
    setSavingImage(true);
    try {
      // Ocultar los botones temporalmente
      const buttonsContainer = invoiceViewRef.current.querySelector('.invoice-actions-container');
      if (buttonsContainer) buttonsContainer.style.display = 'none';
      
      const canvas = await html2canvas(invoiceViewRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true
      });
      
      // Restaurar botones
      if (buttonsContainer) buttonsContainer.style.display = 'flex';
      
      // Crear link de descarga
      const link = document.createElement('a');
      link.download = `Factura_${invoice.invoice_number}_${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success('Imagen guardada correctamente');
    } catch (error) {
      console.error('Error guardando imagen:', error);
      toast.error('Error al guardar la imagen');
    } finally {
      setSavingImage(false);
    }
  };

  return (
    <div>
      {/* Sub-tabs - Mobile Optimized */}
      <div className="flex flex-col sm:flex-row gap-2 mb-6 sm:mb-8 border-b border-white/10 pb-4">
        <div className="flex gap-1 sm:gap-2 overflow-x-auto pb-2 sm:pb-0">
          {[
            { id: 'invoices', label: 'Facturas', icon: FileText },
            { id: 'transactions', label: 'Transacciones', icon: History },
            { id: 'clients', label: 'Clientes', icon: Users },
            { id: 'statistics', label: 'Estadísticas', icon: BarChart3 }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`px-3 sm:px-4 py-2 text-xs uppercase tracking-wider flex items-center gap-1 sm:gap-2 transition-all whitespace-nowrap rounded sm:rounded-none ${
                activeSubTab === tab.id 
                  ? 'bg-white text-black' 
                  : 'text-white/60 hover:text-white border border-white/10'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="hidden xs:inline sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>
        
        <div className="flex gap-2 sm:ml-auto mt-2 sm:mt-0">
          <button
            onClick={() => { fetchProducts(); setShowInvoiceDialog(true); }}
            className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-white text-black text-xs uppercase tracking-wider flex items-center justify-center gap-1 sm:gap-2 hover:bg-gray-200 rounded sm:rounded-none"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Nueva</span> Factura
          </button>
          <button
            onClick={() => setShowPaymentDialog(true)}
            className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-green-500/20 text-green-400 border border-green-500/30 text-xs uppercase tracking-wider flex items-center justify-center gap-1 sm:gap-2 hover:bg-green-500/30 rounded sm:rounded-none"
          >
            <DollarSign className="w-4 h-4" />
            <span className="hidden sm:inline">Registrar</span> Pago
          </button>
          <button
            data-testid="reconcile-billing-btn"
            onClick={handleReconcile}
            disabled={reconciling}
            title="Sincroniza el historial de pagos de cada factura con los pagos vivos. Úsalo si ves pagos eliminados que aún aparecen."
            className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-amber-500/20 text-amber-400 border border-amber-500/30 text-xs uppercase tracking-wider flex items-center justify-center gap-1 sm:gap-2 hover:bg-amber-500/30 rounded sm:rounded-none disabled:opacity-50"
          >
            <ArrowUpDown className="w-4 h-4" />
            <span className="hidden sm:inline">{reconciling ? 'Sincronizando...' : 'Reconciliar'}</span>
          </button>
        </div>
      </div>

      {/* Invoices View */}
      {activeSubTab === 'invoices' && (
        <div className="bg-[#0A0A0A] border border-white/10 overflow-hidden">
          {/* Selection Actions Bar */}
          {selectedInvoices.size > 0 && (
            <div className="bg-blue-500/10 border-b border-blue-500/20 p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-blue-400">{selectedInvoices.size} seleccionada(s)</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={clearInvoiceSelection}
                  className="px-3 py-1.5 text-xs text-white/60 hover:text-white border border-white/10 rounded"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleBulkDeleteCancelled}
                  className="px-3 py-1.5 text-xs bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded flex items-center gap-1"
                >
                  <Trash className="w-3 h-3" />
                  Eliminar Anuladas
                </button>
              </div>
            </div>
          )}
          
          {/* Quick Selection Buttons */}
          <div className="p-3 border-b border-white/10 flex flex-wrap gap-2">
            <button
              onClick={selectAllInvoices}
              className="px-3 py-1.5 text-xs text-white/60 hover:text-white border border-white/10 rounded flex items-center gap-1"
            >
              <CheckSquare className="w-3 h-3" />
              Seleccionar Todo
            </button>
            <button
              onClick={selectCancelledInvoices}
              className="px-3 py-1.5 text-xs text-red-400 hover:bg-red-500/10 border border-red-500/20 rounded flex items-center gap-1"
            >
              <Square className="w-3 h-3" />
              Solo Anuladas
            </button>
            {selectedInvoices.size > 0 && (
              <button
                onClick={clearInvoiceSelection}
                className="px-3 py-1.5 text-xs text-white/40 hover:text-white border border-white/10 rounded"
              >
                Limpiar ({selectedInvoices.size})
              </button>
            )}
          </div>
          
          {/* Filtros por estado */}
          <div className="p-3 border-b border-white/10 flex flex-wrap gap-2 bg-[#050505]">
            <span className="text-xs text-white/40 self-center mr-2">Filtrar:</span>
            {[
              { id: 'all', label: 'Todas', color: 'border-white/20 text-white/60 hover:text-white' },
              { id: 'pendiente', label: 'Pendientes', color: 'border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10' },
              { id: 'parcial', label: 'Parciales', color: 'border-orange-500/30 text-orange-400 hover:bg-orange-500/10' },
              { id: 'pagada', label: 'Pagadas', color: 'border-green-500/30 text-green-400 hover:bg-green-500/10' },
              { id: 'anulada', label: 'Anuladas', color: 'border-red-500/30 text-red-400 hover:bg-red-500/10' }
            ].map(filter => (
              <button
                key={filter.id}
                onClick={() => setInvoiceStatusFilter(filter.id)}
                className={`px-3 py-1.5 text-xs border rounded transition-all ${
                  invoiceStatusFilter === filter.id 
                    ? `${filter.color} bg-white/10 font-semibold` 
                    : filter.color
                }`}
              >
                {filter.label}
                {filter.id !== 'all' && (
                  <span className="ml-1 text-[10px] opacity-70">
                    ({invoices.filter(i => i.status === filter.id).length})
                  </span>
                )}
              </button>
            ))}
          </div>
          
          {/* Mobile Card View */}
          <div className="block sm:hidden divide-y divide-white/5">
            {filteredInvoices.map(inv => (
              <div key={inv.id} className={`p-4 hover:bg-white/5 ${selectedInvoices.has(inv.id) ? 'bg-blue-500/10' : ''}`}>
                <div className="flex items-start gap-3">
                  {/* Checkbox */}
                  <button 
                    onClick={() => toggleInvoiceSelection(inv.id)}
                    className="mt-1 flex-shrink-0"
                  >
                    {selectedInvoices.has(inv.id) ? (
                      <CheckSquare className="w-5 h-5 text-blue-400" />
                    ) : (
                      <Square className="w-5 h-5 text-white/30 hover:text-white/60" />
                    )}
                  </button>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-mono text-sm font-semibold">#{inv.invoice_number}</p>
                        <p className="text-xs text-white/40">{new Date(inv.created_at).toLocaleDateString('es-CO')}</p>
                      </div>
                      <span className={`px-2 py-0.5 text-xs uppercase rounded ${getStatusBadge(inv.status)}`}>
                        {inv.status}
                      </span>
                    </div>
                    <p className="text-sm mb-2">{inv.client_name}</p>
                    <div className="flex justify-between items-center text-sm mb-3">
                      <span className="text-white/60">Total: <span className="text-white font-semibold">${inv.total.toLocaleString()}</span></span>
                      <span className="text-red-400 font-semibold">Saldo: ${inv.balance_due.toLocaleString()}</span>
                    </div>
                    <div className="flex gap-1 pt-2 border-t border-white/5">
                      <button onClick={() => setShowInvoiceView(inv)} className="flex-1 py-2 text-xs bg-white/5 hover:bg-white/10 rounded flex items-center justify-center gap-1">
                        <Eye className="w-3 h-3" /> Ver
                      </button>
                      <button onClick={() => printInvoice(inv)} className="flex-1 py-2 text-xs bg-white/5 hover:bg-white/10 rounded flex items-center justify-center gap-1">
                        <Printer className="w-3 h-3" /> Imprimir
                      </button>
                      <button onClick={() => shareWhatsApp(inv)} className="flex-1 py-2 text-xs bg-green-500/20 text-green-400 rounded flex items-center justify-center gap-1">
                        <Send className="w-3 h-3" /> WA
                      </button>
                      {inv.status !== 'anulada' && (
                        <button onClick={() => startEditInvoice(inv)} className="py-2 px-3 text-xs bg-yellow-500/20 text-yellow-400 rounded">
                          <Edit className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {invoices.length === 0 && (
              <div className="p-8 text-center text-white/40">No hay facturas</div>
            )}
          </div>
          
          {/* Desktop Table View */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="w-10 p-4">
                    <button onClick={() => selectedInvoices.size === invoices.length ? clearInvoiceSelection() : selectAllInvoices()}>
                      {selectedInvoices.size === invoices.length && invoices.length > 0 ? (
                        <CheckSquare className="w-4 h-4 text-blue-400" />
                      ) : (
                        <Square className="w-4 h-4 text-white/30 hover:text-white/60" />
                      )}
                    </button>
                  </th>
                  <th className="text-left text-xs uppercase tracking-wider text-white/40 p-4">#</th>
                  <th className="text-left text-xs uppercase tracking-wider text-white/40 p-4">Fecha</th>
                  <th className="text-left text-xs uppercase tracking-wider text-white/40 p-4">Cliente</th>
                  <th className="text-left text-xs uppercase tracking-wider text-white/40 p-4">Tipo</th>
                  <th className="text-right text-xs uppercase tracking-wider text-white/40 p-4">Total</th>
                  <th className="text-right text-xs uppercase tracking-wider text-white/40 p-4">Pagado</th>
                  <th className="text-right text-xs uppercase tracking-wider text-white/40 p-4">Saldo</th>
                  <th className="text-center text-xs uppercase tracking-wider text-white/40 p-4">Estado</th>
                  <th className="text-center text-xs uppercase tracking-wider text-white/40 p-4">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map(inv => (
                  <tr key={inv.id} className={`border-b border-white/5 hover:bg-white/5 ${selectedInvoices.has(inv.id) ? 'bg-blue-500/10' : ''}`}>
                    <td className="p-4">
                      <button onClick={() => toggleInvoiceSelection(inv.id)}>
                        {selectedInvoices.has(inv.id) ? (
                          <CheckSquare className="w-4 h-4 text-blue-400" />
                        ) : (
                          <Square className="w-4 h-4 text-white/30 hover:text-white/60" />
                        )}
                      </button>
                    </td>
                    <td className="p-4 font-mono text-sm">{inv.invoice_number}</td>
                    <td className="p-4 text-xs text-white/60">{new Date(inv.created_at).toLocaleDateString('es-CO')}</td>
                    <td className="p-4 text-sm">{inv.client_name}</td>
                    <td className="p-4 text-xs uppercase">{inv.invoice_type}</td>
                    <td className="p-4 text-right font-semibold">${inv.total.toLocaleString()}</td>
                    <td className="p-4 text-right text-green-400">${inv.amount_paid.toLocaleString()}</td>
                    <td className="p-4 text-right text-red-400">${inv.balance_due.toLocaleString()}</td>
                    <td className="p-4 text-center">
                      <span className={`px-2 py-1 text-xs uppercase ${getStatusBadge(inv.status)}`}>
                        {inv.status}
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex justify-center gap-1">
                        <button onClick={() => setShowInvoiceView(inv)} className="p-1 hover:bg-white/10" title="Ver">
                          <Eye className="w-4 h-4" />
                        </button>
                        {inv.status !== 'anulada' && (
                          <button onClick={() => startEditInvoice(inv)} className="p-1 hover:bg-white/10 text-yellow-400" title="Editar">
                            <Edit className="w-4 h-4" />
                          </button>
                        )}
                        <button onClick={() => printInvoice(inv)} className="p-1 hover:bg-white/10" title="Imprimir">
                          <Printer className="w-4 h-4" />
                        </button>
                        <button onClick={() => shareWhatsApp(inv)} className="p-1 hover:bg-white/10 text-green-400" title="WhatsApp">
                          <Send className="w-4 h-4" />
                        </button>
                        {inv.status !== 'anulada' ? (
                          <button 
                            onClick={() => {
                              setShowInvoiceDeleteConfirm(inv);
                              setInvoiceDeleteAction('cancel');
                            }} 
                            className="p-1 hover:bg-red-500/20 text-red-400" 
                            title="Anular factura"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        ) : (
                          <button 
                            onClick={() => {
                              setShowInvoiceDeleteConfirm(inv);
                              setInvoiceDeleteAction('delete');
                            }} 
                            className="p-1 hover:bg-red-500/20 text-red-400" 
                            title="Eliminar definitivamente"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {invoices.length === 0 && (
                  <tr><td colSpan="9" className="p-8 text-center text-white/40">No hay facturas</td></tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Resumen de totales filtrados */}
          <div className="mt-4 p-3 sm:p-4 bg-[#0A0A0A] border border-white/10">
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-4">
              <div className="text-sm text-white/60 text-center sm:text-left">
                <span className="font-semibold text-white">{filteredTotals.count}</span> factura(s) 
                {invoiceStatusFilter !== 'all' && (
                  <span className="ml-1">
                    ({invoiceStatusFilter === 'pendiente' ? 'Pendientes' : 
                      invoiceStatusFilter === 'parcial' ? 'Parciales' : 
                      invoiceStatusFilter === 'pagada' ? 'Pagadas' : 'Anuladas'})
                  </span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2 sm:flex sm:flex-wrap sm:gap-4 md:gap-8 text-sm">
                <div className="text-center p-2 bg-white/5 sm:bg-transparent sm:p-0">
                  <p className="text-[9px] sm:text-[10px] uppercase text-white/40">Facturado</p>
                  <p className="text-sm sm:text-lg font-bold text-white">${filteredTotals.total.toLocaleString()}</p>
                </div>
                <div className="text-center p-2 bg-white/5 sm:bg-transparent sm:p-0">
                  <p className="text-[9px] sm:text-[10px] uppercase text-white/40">Pagado</p>
                  <p className="text-sm sm:text-lg font-bold text-green-400">${filteredTotals.paid.toLocaleString()}</p>
                </div>
                <div className="text-center p-2 bg-white/5 sm:bg-transparent sm:p-0">
                  <p className="text-[9px] sm:text-[10px] uppercase text-white/40">Pendiente</p>
                  <p className="text-sm sm:text-lg font-bold text-red-400">${filteredTotals.balance.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transactions View */}
      {activeSubTab === 'transactions' && (
        <div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <History className="w-5 h-5" />
                Historial de Transacciones
              </h2>
              <p className="text-sm text-white/60 mt-1">{filteredTransactions.length} transacciones</p>
            </div>
            
            {/* Filtros */}
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'all', label: 'Todas' },
                { id: 'factura', label: 'Facturas' },
                { id: 'pago', label: 'Pagos' }
              ].map(filter => (
                <button
                  key={filter.id}
                  onClick={() => setTransactionFilter(filter.id)}
                  className={`px-3 py-1.5 text-xs uppercase tracking-wider transition-all ${
                    transactionFilter === filter.id
                      ? 'bg-white text-black'
                      : 'bg-white/5 text-white/60 hover:text-white border border-white/10'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          {/* Lista de transacciones */}
          <div className="space-y-3">
            {filteredTransactions.length === 0 ? (
              <div className="text-center py-12 text-white/40">
                <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No hay transacciones para mostrar</p>
              </div>
            ) : (
              filteredTransactions.map(transaction => (
                <div 
                  key={`${transaction.type}-${transaction.id}`}
                  className={`bg-[#0A0A0A] border p-4 ${
                    transaction.type === 'factura' 
                      ? 'border-blue-500/20 hover:border-blue-500/40' 
                      : 'border-green-500/20 hover:border-green-500/40'
                  } transition-all`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    {/* Info principal */}
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`p-2 rounded ${
                        transaction.type === 'factura' ? 'bg-blue-500/20' : 'bg-green-500/20'
                      }`}>
                        {transaction.type === 'factura' ? (
                          <FileText className="w-5 h-5 text-blue-400" />
                        ) : (
                          <DollarSign className="w-5 h-5 text-green-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`text-xs uppercase px-2 py-0.5 ${
                            transaction.type === 'factura' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'
                          }`}>
                            {transaction.type}
                          </span>
                          {transaction.invoice_number && (
                            <span className="text-sm font-semibold">#{transaction.invoice_number}</span>
                          )}
                          {transaction.status && (
                            <span className={`text-xs uppercase px-2 py-0.5 ${
                              transaction.status === 'pagada' ? 'bg-green-500/20 text-green-400' :
                              transaction.status === 'parcial' ? 'bg-yellow-500/20 text-yellow-400' :
                              transaction.status === 'anulada' ? 'bg-red-500/20 text-red-400' :
                              transaction.status === 'aplicado' ? 'bg-green-500/20 text-green-400' :
                              'bg-gray-500/20 text-gray-400'
                            }`}>
                              {transaction.status}
                            </span>
                          )}
                        </div>
                        <p className="text-sm mt-1">{transaction.description}</p>
                        <p className="text-xs text-white/50 mt-1">
                          {transaction.client_name} • {new Date(transaction.date).toLocaleDateString('es-CO', { 
                            day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                          })}
                        </p>
                        {transaction.type === 'pago' && transaction.applied_to && transaction.applied_to.length > 0 && (
                          <p className="text-xs text-white/40 mt-1">
                            Aplicado a: {transaction.applied_to.map(a => `#${a.invoice_number || a.invoice_id}`).join(', ')}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Monto y acciones */}
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className={`text-lg font-bold ${
                          transaction.type === 'factura' ? 'text-blue-400' : 'text-green-400'
                        }`}>
                          {transaction.type === 'factura' ? '' : '+'}${transaction.amount.toLocaleString()}
                        </p>
                        {transaction.type === 'factura' && transaction.balance_due > 0 && (
                          <p className="text-xs text-red-400">Saldo: ${transaction.balance_due.toLocaleString()}</p>
                        )}
                      </div>
                      
                      {/* Botones de acción */}
                      <div className="flex gap-1">
                        {transaction.type === 'factura' ? (
                          <>
                            <button
                              onClick={() => setShowInvoiceView(transaction.data)}
                              className="p-2 hover:bg-white/10 transition-colors"
                              title="Ver factura"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setEditingInvoice(transaction.data);
                                setInvoiceForm({
                                  client_id: transaction.data.client_id,
                                  invoice_type: transaction.data.invoice_type,
                                  display_mode: transaction.data.display_mode || 'detail',
                                  items: transaction.data.items || [],
                                  discount: transaction.data.discount || 0,
                                  tax: transaction.data.tax || 0,
                                  notes: transaction.data.notes || '',
                                  initial_payment: 0,
                                  payment_method: 'efectivo'
                                });
                                fetchProducts();
                                setShowInvoiceDialog(true);
                              }}
                              className="p-2 hover:bg-white/10 transition-colors"
                              title="Editar factura"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleEditPayment(transaction)}
                              className="p-2 hover:bg-white/10 transition-colors"
                              title="Editar pago"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setShowDeleteConfirm(transaction)}
                              className="p-2 hover:bg-red-500/20 text-red-400 transition-colors"
                              title="Eliminar pago"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Resumen de transacciones */}
          <div className="mt-6 p-4 bg-[#0A0A0A] border border-white/10">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
              <div className="p-2 bg-white/5">
                <p className="text-[10px] uppercase text-white/40">Total Transacciones</p>
                <p className="text-lg font-bold">{transactions.length}</p>
              </div>
              <div className="p-2 bg-blue-500/10">
                <p className="text-[10px] uppercase text-white/40">Facturas</p>
                <p className="text-lg font-bold text-blue-400">{transactions.filter(t => t.type === 'factura').length}</p>
              </div>
              <div className="p-2 bg-green-500/10">
                <p className="text-[10px] uppercase text-white/40">Pagos</p>
                <p className="text-lg font-bold text-green-400">{transactions.filter(t => t.type === 'pago').length}</p>
              </div>
              <div className="p-2 bg-green-500/10">
                <p className="text-[10px] uppercase text-white/40">Total Pagado</p>
                <p className="text-lg font-bold text-green-400">
                  ${transactions.filter(t => t.type === 'pago').reduce((sum, t) => sum + t.amount, 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Clients View */}
      {activeSubTab === 'clients' && (
        <div>
          <div className="flex justify-end mb-4">
            <button
              onClick={() => { setEditingClient(null); setClientForm({ name: '', phone: '', address: '', document: '', email: '', notes: '' }); setShowClientDialog(true); }}
              className="px-4 py-2 bg-white text-black text-xs uppercase tracking-wider flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Nuevo Cliente
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {clients.map(client => (
              <div
                key={client.id}
                data-testid={`billing-client-card-${client.id}`}
                onClick={() => setClientDetailId(client.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') setClientDetailId(client.id); }}
                className="bg-[#0A0A0A] border border-white/10 p-4 cursor-pointer hover:border-white/30 hover:bg-white/[0.02] transition-colors"
                title="Click para ver detalle de cuenta"
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-semibold">{client.name}</h3>
                    {client.document && <p className="text-xs text-white/40">{client.document}</p>}
                  </div>
                  <span className={`text-lg font-bold ${getBalanceColor(client.balance)}`}>
                    ${Math.abs(client.balance).toLocaleString()}
                    <span className="text-xs ml-1">{client.balance > 0 ? 'a favor' : client.balance < 0 ? 'debe' : ''}</span>
                  </span>
                </div>
                
                <div className="text-xs text-white/60 space-y-1 mb-4">
                  {client.phone && <p>Tel: {client.phone}</p>}
                  {client.email && <p>{client.email}</p>}
                  {client.address && <p>{client.address}</p>}
                </div>
                
                <div className="flex justify-between items-center text-xs text-white/40">
                  <span>Compras: ${client.total_purchases?.toLocaleString() || 0}</span>
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setEditingClient(client); setClientForm(client); setShowClientDialog(true); }}
                      className="text-white/60 hover:text-white"
                    >
                      Editar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Statistics View */}
      {activeSubTab === 'statistics' && (
        <StatisticsPanel />
      )}

      {/* Invoice Dialog */}
      {showInvoiceDialog && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto"
          onClick={closeInvoiceDialog}
          data-testid="invoice-dialog-overlay"
        >
          <div
            className="bg-[#0A0A0A] border border-white/10 w-full max-w-4xl my-3 sm:my-6"
            onClick={e => e.stopPropagation()}
            data-testid="invoice-dialog"
          >
            {/* Header sticky con Volver */}
            <div className="sticky top-0 z-10 bg-[#0A0A0A]/95 backdrop-blur border-b border-white/10 px-4 sm:px-6 py-3 flex justify-between items-center">
              <button
                onClick={closeInvoiceDialog}
                data-testid="invoice-back-button"
                className="flex items-center gap-2 text-white/70 hover:text-white text-xs uppercase tracking-[0.2em] px-2 py-1 -ml-2"
                title="Volver"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Volver</span>
              </button>
              <h2 className="text-base sm:text-xl font-semibold flex-1 text-center">
                {editingInvoice ? 'Editar Factura' : 'Nueva Factura'}
              </h2>
              <button
                onClick={closeInvoiceDialog}
                className="p-2 hover:bg-white/10 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body scrolleable */}
            <div className="p-4 sm:p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider mb-2 text-white/60">Cliente *</label>
                  <select
                    value={invoiceForm.client_id}
                    onChange={(e) => setInvoiceForm({...invoiceForm, client_id: e.target.value})}
                    className="w-full bg-[#121212] border border-white/10 px-3 sm:px-4 py-3 text-sm outline-none rounded"
                  >
                    <option value="">Seleccionar cliente</option>
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>{c.name} {c.document ? `(${c.document})` : ''}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs uppercase tracking-wider mb-2 text-white/60">Tipo de Factura</label>
                  <select
                    value={invoiceForm.invoice_type}
                    onChange={(e) => setInvoiceForm({...invoiceForm, invoice_type: e.target.value})}
                    className="w-full bg-[#121212] border border-white/10 px-3 sm:px-4 py-3 text-sm outline-none rounded"
                  >
                    <option value="contado">Contado</option>
                    <option value="credito">Crédito</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs uppercase tracking-wider mb-2 text-white/60">Modo de Visualización</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setInvoiceForm({...invoiceForm, display_mode: 'product'})}
                      className={`flex-1 py-3 text-xs uppercase tracking-wider border transition-all ${
                        invoiceForm.display_mode === 'product'
                          ? 'bg-white text-black border-white'
                          : 'bg-transparent text-white/60 border-white/10 hover:border-white/30'
                      }`}
                    >
                      Por Producto
                    </button>
                    <button
                      type="button"
                      onClick={() => setInvoiceForm({...invoiceForm, display_mode: 'category'})}
                      className={`flex-1 py-3 text-xs uppercase tracking-wider border transition-all ${
                        invoiceForm.display_mode === 'category'
                          ? 'bg-white text-black border-white'
                          : 'bg-transparent text-white/60 border-white/10 hover:border-white/30'
                      }`}
                    >
                      Por Categoría
                    </button>
                  </div>
                  <p className="text-xs text-white/40 mt-2">
                    {invoiceForm.display_mode === 'product' 
                      ? 'La factura mostrará cada producto individualmente'
                      : 'La factura agrupará los productos por categoría'}
                  </p>
                </div>
                
                <div className="relative" ref={productSearchRef}>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs uppercase tracking-wider text-white/60">Agregar Producto</label>
                    <button
                      type="button"
                      onClick={() => setShowVisualPicker(true)}
                      data-testid="open-visual-picker"
                      className="inline-flex items-center gap-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold transition-colors"
                    >
                      <Package className="w-3.5 h-3.5" />
                      Vista Visual
                    </button>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                    <input
                      type="text"
                      value={productSearch}
                      onChange={(e) => {
                        setProductSearch(e.target.value);
                        setShowProductDropdown(true);
                      }}
                      onFocus={() => setShowProductDropdown(true)}
                      placeholder="Buscar por nombre, marca, SKU..."
                      className="w-full bg-[#121212] border border-white/10 pl-10 pr-10 py-3 text-sm outline-none focus:border-white/30"
                    />
                    {productSearch && (
                      <button
                        type="button"
                        onClick={() => {
                          setProductSearch('');
                          setShowProductDropdown(false);
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  
                  {/* Product dropdown - Mejorado con imágenes, zoom y filtros */}
                  {showProductDropdown && (
                    <>
                      <div 
                        className="fixed inset-0 z-40" 
                        onClick={() => setShowProductDropdown(false)}
                      />
                      <div className="absolute top-full left-0 right-0 mt-1 bg-[#1a1a1a] border border-white/10 max-h-[70vh] md:max-h-[500px] overflow-hidden z-50 rounded-lg shadow-xl flex flex-col">
                        {/* Filtros por categoría */}
                        <div className="p-2 bg-[#111] border-b border-white/10 flex flex-wrap gap-1.5 sticky top-0 z-10">
                          <button
                            type="button"
                            onClick={() => setProductCategoryFilter('all')}
                            className={`px-2 py-1 text-[10px] uppercase rounded transition-colors ${productCategoryFilter === 'all' ? 'bg-white/20 text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'}`}
                          >
                            Todas ({products.length})
                          </button>
                          {[...new Set(products.map(p => p.category).filter(Boolean))].sort().map(cat => {
                            const count = products.filter(p => p.category === cat).length;
                            return (
                              <button
                                key={cat}
                                type="button"
                                onClick={() => setProductCategoryFilter(cat)}
                                className={`px-2 py-1 text-[10px] uppercase rounded transition-colors ${productCategoryFilter === cat ? 'bg-white/20 text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'}`}
                              >
                                {cat.replace(/-/g, ' ')} ({count})
                              </button>
                            );
                          })}
                        </div>
                        
                        {/* Lista de productos */}
                        <div className="overflow-y-auto flex-1">
                          {products
                            .filter(p => {
                              // Filtro por categoría
                              if (productCategoryFilter !== 'all' && p.category !== productCategoryFilter) return false;
                              // Filtro por búsqueda
                              if (!productSearch) return true;
                              const search = productSearch.toLowerCase();
                              return (
                                p.name.toLowerCase().includes(search) ||
                                p.category?.toLowerCase().includes(search) ||
                                p.brand?.toLowerCase().includes(search) ||
                                p.sku?.toLowerCase().includes(search)
                              );
                            })
                            .map(p => {
                              const productImage = p.images?.[0] || null;
                              const inventory = p.inventory || { china: 0, contenedor: 0, stock: 0 };
                              const stockDisponible = p.stock || inventory.stock || 0;
                              const totalInventory = (inventory.china || 0) + (inventory.contenedor || 0) + stockDisponible;
                              
                              return (
                                <div
                                  key={p.id}
                                  className="w-full px-3 py-2 hover:bg-white/5 transition-colors flex items-center gap-3 border-b border-white/5 last:border-0"
                                >
                                  {/* Imagen del producto con zoom */}
                                  <div 
                                    className="w-12 h-12 md:w-14 md:h-14 flex-shrink-0 bg-[#0A0A0A] border border-white/10 rounded overflow-hidden cursor-pointer hover:border-white/30 transition-colors relative group"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (productImage) setZoomImage(productImage);
                                    }}
                                  >
                                    {productImage ? (
                                      <>
                                        <img 
                                          src={productImage} 
                                          alt={p.name}
                                          className="w-full h-full object-cover"
                                          onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                          <Search className="w-4 h-4 text-white" />
                                        </div>
                                      </>
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center text-white/20">
                                        <Package className="w-5 h-5" />
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Info del producto - clickeable para agregar */}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      addItemToInvoice(p);
                                      setProductSearch('');
                                      setShowProductDropdown(false);
                                    }}
                                    className="flex-1 min-w-0 text-left"
                                  >
                                    <p className="text-sm font-medium truncate">{p.name}</p>
                                    <p className="text-xs text-white/40 truncate">{p.brand} • {p.category}</p>
                                  </button>
                                  
                                  {/* Precio y Stock - clickeable para agregar */}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      addItemToInvoice(p);
                                      setProductSearch('');
                                      setShowProductDropdown(false);
                                    }}
                                    className="text-right flex-shrink-0"
                                  >
                                    <p className="text-sm font-semibold text-emerald-400">${p.price?.toLocaleString()}</p>
                                    <p className={`text-xs ${stockDisponible > 0 ? 'text-white/40' : 'text-orange-400'}`}>
                                      Stock: {stockDisponible}
                                    </p>
                                    {totalInventory > stockDisponible && (
                                      <p className="text-[10px] text-blue-400">+{totalInventory - stockDisponible} bodega</p>
                                    )}
                                  </button>
                                </div>
                              );
                            })}
                          {products.filter(p => {
                            if (productCategoryFilter !== 'all' && p.category !== productCategoryFilter) return false;
                            if (!productSearch) return true;
                            const search = productSearch.toLowerCase();
                            return p.name.toLowerCase().includes(search) || p.category?.toLowerCase().includes(search) || p.brand?.toLowerCase().includes(search) || p.sku?.toLowerCase().includes(search);
                          }).length === 0 && (
                            <p className="px-4 py-6 text-sm text-white/40 text-center">No se encontraron productos</p>
                          )}
                        </div>
                        
                        {/* Footer con info */}
                        <div className="px-4 py-2 text-xs text-white/30 text-center bg-[#111] border-t border-white/5 sticky bottom-0">
                          {products.filter(p => productCategoryFilter === 'all' || p.category === productCategoryFilter).length} productos • Click en imagen para zoom
                        </div>
                      </div>
                    </>
                  )}
                </div>
                
                {/* Botón para agregar Saldo Anterior */}
                <div>
                  <button
                    type="button"
                    onClick={() => {
                      // Check if saldo anterior already exists in items
                      const existingSaldo = invoiceForm.items.find(item => item.product_id === 'SALDO_ANTERIOR');
                      if (existingSaldo) {
                        toast.error('Ya existe un Saldo Anterior en esta factura');
                        return;
                      }
                      
                      const amount = prompt('Ingrese el monto del saldo anterior (deuda):');
                      if (amount && !isNaN(parseFloat(amount)) && parseFloat(amount) > 0) {
                        const saldoItem = {
                          product_id: 'SALDO_ANTERIOR',
                          product_name: 'Saldo Anterior',
                          item_type: 'saldo_anterior',
                          quantity: 1,
                          unit_price: parseFloat(amount),
                          max_stock: 999
                        };
                        setInvoiceForm({...invoiceForm, items: [...invoiceForm.items, saldoItem]});
                        toast.success(`Saldo anterior de $${parseFloat(amount).toLocaleString()} agregado`);
                      } else if (amount !== null) {
                        toast.error('Ingrese un monto válido mayor a 0');
                      }
                    }}
                    className="w-full bg-amber-600 hover:bg-amber-500 text-black py-3 text-xs uppercase tracking-wider font-semibold flex items-center justify-center gap-2"
                  >
                    <DollarSign className="w-4 h-4" />
                    Agregar Saldo Anterior (Deuda)
                  </button>
                  <p className="text-xs text-white/40 mt-1">
                    Use esto para registrar deudas anteriores que no están en el sistema
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs uppercase tracking-wider mb-2 text-white/60">Descuento</label>
                    <input
                      type="number"
                      min="0"
                      value={invoiceForm.discount}
                      onChange={(e) => setInvoiceForm({...invoiceForm, discount: parseFloat(e.target.value) || 0})}
                      className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-sm outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider mb-2 text-white/60">Impuesto</label>
                    <input
                      type="number"
                      min="0"
                      value={invoiceForm.tax}
                      onChange={(e) => setInvoiceForm({...invoiceForm, tax: parseFloat(e.target.value) || 0})}
                      className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-sm outline-none"
                    />
                  </div>
                </div>
                
                {invoiceForm.invoice_type === 'contado' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-wider mb-2 text-white/60">Pago Inicial</label>
                      <input
                        type="number"
                        min="0"
                        value={invoiceForm.initial_payment}
                        onChange={(e) => setInvoiceForm({...invoiceForm, initial_payment: parseFloat(e.target.value) || 0})}
                        className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-sm outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wider mb-2 text-white/60">Método</label>
                      <select
                        value={invoiceForm.payment_method}
                        onChange={(e) => setInvoiceForm({...invoiceForm, payment_method: e.target.value})}
                        className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-sm outline-none"
                      >
                        <option value="efectivo">Efectivo</option>
                        <option value="transferencia">Transferencia</option>
                        <option value="tarjeta">Tarjeta</option>
                      </select>
                    </div>
                  </div>
                )}
                
                <div>
                  <label className="block text-xs uppercase tracking-wider mb-2 text-white/60">Notas</label>
                  <textarea
                    value={invoiceForm.notes}
                    onChange={(e) => setInvoiceForm({...invoiceForm, notes: e.target.value})}
                    rows="2"
                    className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-sm outline-none"
                  />
                </div>
              </div>
              
              <div>
                <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                  <h3 className="text-xs uppercase tracking-wider text-white/60">
                    Detalle de Factura
                    {editingInvoice && <span className="ml-2 text-yellow-400">(Editando)</span>}
                  </h3>
                  {invoiceForm.items.filter(i => i.item_type !== 'saldo_anterior').length > 0 && (
                    <div className="flex items-center gap-2">
                      {(() => {
                        const selectableItems = invoiceForm.items.filter(i => i.item_type !== 'saldo_anterior');
                        const allSelected = selectableItems.length > 0 && selectableItems.every(i => selectedItemIds.includes(i.product_id));
                        return (
                          <button
                            type="button"
                            onClick={() => {
                              if (allSelected) setSelectedItemIds([]);
                              else setSelectedItemIds(selectableItems.map(i => i.product_id));
                            }}
                            data-testid="select-all-items"
                            className="text-[10px] uppercase tracking-wider text-white/60 hover:text-white flex items-center gap-1.5 px-2 py-1 border border-white/10 hover:border-white/30 rounded"
                            title={allSelected ? 'Deseleccionar todos' : 'Seleccionar todos'}
                          >
                            {allSelected ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                            Todos
                          </button>
                        );
                      })()}
                      <button
                        type="button"
                        onClick={() => {
                          if (selectedItemIds.length === 0) {
                            toast.error('Selecciona uno o más productos con el checkbox');
                            return;
                          }
                          setBulkPriceInput('');
                          setShowBulkPriceModal(true);
                        }}
                        data-testid="open-bulk-price"
                        className="text-[10px] uppercase tracking-wider bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 text-emerald-300 px-2.5 py-1 rounded font-semibold flex items-center gap-1.5"
                      >
                        <Tag className="w-3.5 h-3.5" />
                        Cambiar precio {selectedItemIds.length > 0 ? `(${selectedItemIds.length})` : ''}
                      </button>
                    </div>
                  )}
                </div>
                <div className="bg-[#121212] border border-white/10 p-4 min-h-[300px]">
                  {invoiceForm.items.length === 0 ? (
                    <p className="text-white/40 text-center py-8">Agrega productos a la factura</p>
                  ) : (
                    <div className="space-y-3">
                      {invoiceForm.items.map((item, idx) => (
                        <div key={idx} className={`flex flex-col sm:flex-row sm:items-center justify-between border-b pb-3 gap-2 ${item.item_type === 'saldo_anterior' ? 'border-amber-500/30 bg-amber-500/5 p-2 -m-2' : 'border-white/5'}`}>
                          <div className="flex-1 flex items-center gap-3">
                            {/* Checkbox de selección (no aplica a saldo anterior) */}
                            {item.item_type !== 'saldo_anterior' && (
                              <button
                                type="button"
                                onClick={() => setSelectedItemIds(prev => prev.includes(item.product_id) ? prev.filter(id => id !== item.product_id) : [...prev, item.product_id])}
                                data-testid={`select-item-${item.product_id}`}
                                className="flex-shrink-0 text-white/60 hover:text-white"
                                title="Seleccionar para cambio de precio en lote"
                              >
                                {selectedItemIds.includes(item.product_id) ? (
                                  <CheckSquare className="w-5 h-5 text-emerald-400" />
                                ) : (
                                  <Square className="w-5 h-5" />
                                )}
                              </button>
                            )}
                            {/* Thumbnail (no aplica a saldo anterior) */}
                            {item.item_type !== 'saldo_anterior' && (
                              <button
                                type="button"
                                onClick={() => item.product_image && setZoomImage(item.product_image)}
                                data-testid={`invoice-item-thumb-${item.product_id}`}
                                className="w-14 h-14 sm:w-16 sm:h-16 flex-shrink-0 bg-[#0A0A0A] border border-white/10 hover:border-white/40 rounded overflow-hidden relative group transition-colors"
                              >
                                {item.product_image ? (
                                  <>
                                    <img
                                      src={item.product_image}
                                      alt={item.product_name}
                                      className="w-full h-full object-cover"
                                      onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                      <Search className="w-4 h-4 text-white" />
                                    </div>
                                  </>
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-white/20">
                                    <Package className="w-5 h-5" />
                                  </div>
                                )}
                              </button>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${item.item_type === 'saldo_anterior' ? 'text-amber-400 font-semibold' : ''}`}>
                                {item.item_type === 'saldo_anterior' && <DollarSign className="w-4 h-4 inline mr-1" />}
                                {item.product_name}
                              </p>
                              {/* Precio editable */}
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-[10px] text-white/40 uppercase">
                                  {item.item_type === 'saldo_anterior' ? 'Monto:' : 'Precio:'}
                                </span>
                                <input
                                  type="number"
                                  value={item.unit_price}
                                  onChange={(e) => updateItemPrice(item.product_id, e.target.value)}
                                  className={`w-24 bg-[#0A0A0A] border px-2 py-1 text-xs outline-none focus:border-white/30 ${item.item_type === 'saldo_anterior' ? 'border-amber-500/30' : 'border-white/10'}`}
                                  min="0"
                                  step="1"
                                />
                                {item.item_type !== 'saldo_anterior' && <span className="text-xs text-white/40">c/u</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 sm:gap-2">
                            {item.item_type !== 'saldo_anterior' && (
                              <div className="flex items-center bg-[#0A0A0A] rounded overflow-hidden border border-white/10">
                                <button
                                  onClick={() => {
                                    const newItems = [...invoiceForm.items];
                                    if (newItems[idx].quantity > 1) {
                                      newItems[idx].quantity -= 1;
                                      setInvoiceForm({...invoiceForm, items: newItems});
                                    }
                                  }}
                                  className="w-8 h-8 sm:w-6 sm:h-6 flex items-center justify-center hover:bg-white/10 text-lg"
                                >-</button>
                                <input
                                  type="number"
                                  min="1"
                                  max={item.max_stock}
                                  value={item.quantity}
                                  onChange={(e) => {
                                    const newItems = [...invoiceForm.items];
                                    const val = parseInt(e.target.value) || 1;
                                    newItems[idx].quantity = Math.max(1, Math.min(val, item.max_stock));
                                    setInvoiceForm({...invoiceForm, items: newItems});
                                  }}
                                  className="w-12 sm:w-14 text-center text-sm font-medium bg-transparent border-none outline-none"
                                />
                                <button
                                  onClick={() => {
                                    const newItems = [...invoiceForm.items];
                                    if (newItems[idx].quantity < item.max_stock) {
                                      newItems[idx].quantity += 1;
                                      setInvoiceForm({...invoiceForm, items: newItems});
                                    }
                                  }}
                                  className="w-8 h-8 sm:w-6 sm:h-6 flex items-center justify-center hover:bg-white/10 text-lg"
                                >+</button>
                              </div>
                            )}
                            <span className={`w-20 sm:w-24 text-right font-semibold text-sm ${item.item_type === 'saldo_anterior' ? 'text-amber-400' : ''}`}>
                              ${(item.quantity * item.unit_price).toLocaleString()}
                            </span>
                            <button
                              onClick={() => {
                                setInvoiceForm({...invoiceForm, items: invoiceForm.items.filter((_, i) => i !== idx)});
                              }}
                              className="text-red-400 ml-1 sm:ml-2 hover:text-red-300 p-1"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                      
                      <div className="border-t border-white/10 pt-4 mt-4 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Subtotal</span>
                          <span>${invoiceForm.items.reduce((s, i) => s + i.quantity * i.unit_price, 0).toLocaleString()}</span>
                        </div>
                        {invoiceForm.discount > 0 && (
                          <div className="flex justify-between text-sm text-red-400">
                            <span>Descuento</span>
                            <span>-${invoiceForm.discount.toLocaleString()}</span>
                          </div>
                        )}
                        {invoiceForm.tax > 0 && (
                          <div className="flex justify-between text-sm">
                            <span>Impuesto</span>
                            <span>${invoiceForm.tax.toLocaleString()}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-lg font-bold border-t border-white/10 pt-2">
                          <span>TOTAL</span>
                          <span>${calculateInvoiceTotal().toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex gap-4 mt-6">
              <button onClick={handleCreateInvoice} className="flex-1 bg-white text-black py-3 text-xs uppercase tracking-wider font-semibold hover:bg-gray-200">
                {editingInvoice ? 'Guardar Cambios' : 'Crear Factura'}
              </button>
              <button 
                onClick={closeInvoiceDialog} 
                className="flex-1 border border-white/20 py-3 text-xs uppercase tracking-wider hover:bg-white/5"
              >
                Cancelar
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Client Dialog */}
      {showClientDialog && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowClientDialog(false)}>
          <div className="bg-[#0A0A0A] border border-white/10 p-6 max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-semibold mb-6">{editingClient ? 'Editar Cliente' : 'Nuevo Cliente'}</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-wider mb-2 text-white/60">Nombre *</label>
                <input
                  type="text"
                  value={clientForm.name}
                  onChange={(e) => setClientForm({...clientForm, name: e.target.value})}
                  className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-sm outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider mb-2 text-white/60">Teléfono</label>
                  <input
                    type="text"
                    value={clientForm.phone}
                    onChange={(e) => setClientForm({...clientForm, phone: e.target.value})}
                    className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-sm outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider mb-2 text-white/60">Documento</label>
                  <input
                    type="text"
                    value={clientForm.document}
                    onChange={(e) => setClientForm({...clientForm, document: e.target.value})}
                    className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-sm outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider mb-2 text-white/60">Email</label>
                <input
                  type="email"
                  value={clientForm.email}
                  onChange={(e) => setClientForm({...clientForm, email: e.target.value})}
                  className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-sm outline-none"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider mb-2 text-white/60">Dirección</label>
                <input
                  type="text"
                  value={clientForm.address}
                  onChange={(e) => setClientForm({...clientForm, address: e.target.value})}
                  className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-sm outline-none"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider mb-2 text-white/60">Notas</label>
                <textarea
                  value={clientForm.notes}
                  onChange={(e) => setClientForm({...clientForm, notes: e.target.value})}
                  rows="2"
                  className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-sm outline-none"
                />
              </div>
            </div>
            
            <div className="flex gap-4 mt-6">
              <button onClick={handleCreateClient} className="flex-1 bg-white text-black py-3 text-xs uppercase tracking-wider font-semibold">
                {editingClient ? 'Guardar Cambios' : 'Crear Cliente'}
              </button>
              <button onClick={() => { setShowClientDialog(false); setEditingClient(null); }} className="flex-1 border border-white/20 py-3 text-xs uppercase tracking-wider">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Dialog */}
      {showPaymentDialog && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowPaymentDialog(false)}>
          <div className="bg-[#0A0A0A] border border-white/10 p-6 max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-semibold mb-6">Registrar Pago / Abono</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-wider mb-2 text-white/60">Cliente *</label>
                <select
                  value={paymentForm.client_id}
                  onChange={(e) => setPaymentForm({...paymentForm, client_id: e.target.value})}
                  className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-sm outline-none"
                >
                  <option value="">Seleccionar cliente</option>
                  {clients.filter(c => c.balance < 0).map(c => (
                    <option key={c.id} value={c.id}>{c.name} - Debe: ${Math.abs(c.balance).toLocaleString()}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider mb-2 text-white/60">Monto *</label>
                <input
                  type="number"
                  min="0"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({...paymentForm, amount: parseFloat(e.target.value) || 0})}
                  className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-sm outline-none"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider mb-2 text-white/60">Método de Pago</label>
                <select
                  value={paymentForm.payment_method}
                  onChange={(e) => setPaymentForm({...paymentForm, payment_method: e.target.value})}
                  className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-sm outline-none"
                >
                  <option value="efectivo">Efectivo</option>
                  <option value="transferencia">Transferencia</option>
                  <option value="tarjeta">Tarjeta</option>
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider mb-2 text-white/60">Notas</label>
                <textarea
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                  rows="2"
                  className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-sm outline-none"
                />
              </div>
              <p className="text-xs text-white/40">
                * El pago se aplicará automáticamente a la factura más antigua del cliente
              </p>
            </div>
            
            <div className="flex gap-4 mt-6">
              <button onClick={handleCreatePayment} className="flex-1 bg-green-500 text-black py-3 text-xs uppercase tracking-wider font-semibold hover:bg-green-400">
                Registrar Pago
              </button>
              <button onClick={() => setShowPaymentDialog(false)} className="flex-1 border border-white/20 py-3 text-xs uppercase tracking-wider">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice View Dialog */}
      {showInvoiceView && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4" onClick={() => setShowInvoiceView(null)}>
          <div ref={invoiceViewRef} className="bg-white text-black p-4 sm:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="text-center mb-6 sm:mb-8 border-b pb-4 sm:pb-6">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-[0.2em]">HAUTE</h1>
              <p className="text-gray-500 text-sm tracking-[0.2em] uppercase">Distribuidor Mayorista de Moda Premium</p>
            </div>
            
            <div className="flex flex-col sm:flex-row sm:justify-between gap-4 sm:gap-0 mb-6 sm:mb-8">
              <div className="text-center sm:text-left">
                <h3 className="text-xs uppercase text-gray-400 mb-2">Factura</h3>
                <p className="text-xl sm:text-2xl font-bold">#{showInvoiceView.invoice_number}</p>
                <p className="text-sm text-gray-500">{new Date(showInvoiceView.created_at).toLocaleDateString('es-CO')}</p>
                <div className="flex flex-wrap gap-2 mt-2 justify-center sm:justify-start">
                  <span className={`inline-block px-3 py-1 text-xs uppercase ${
                    showInvoiceView.status === 'pagada' ? 'bg-green-100 text-green-800' :
                    showInvoiceView.status === 'parcial' ? 'bg-yellow-100 text-yellow-800' :
                    showInvoiceView.status === 'anulada' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {showInvoiceView.status}
                  </span>
                  {showInvoiceView.display_mode === 'category' && (
                    <span className="inline-block px-3 py-1 text-xs uppercase bg-blue-100 text-blue-800">
                      Agrupado
                    </span>
                  )}
                </div>
              </div>
              <div className="text-center sm:text-right">
                <h3 className="text-xs uppercase text-gray-400 mb-2">Cliente</h3>
                <p className="font-semibold">{showInvoiceView.client_name}</p>
                {showInvoiceView.client_document && <p className="text-sm">{showInvoiceView.client_document}</p>}
                {showInvoiceView.client_phone && <p className="text-sm">{showInvoiceView.client_phone}</p>}
              </div>
            </div>
            
            {/* Renderizar según modo de visualización */}
            {showInvoiceView.display_mode === 'category' && showInvoiceView.items_grouped && showInvoiceView.items_grouped.length > 0 ? (
              /* Vista agrupada por categoría */
              <table className="w-full mb-8">
                <thead>
                  <tr className="border-b-2 border-black">
                    <th className="text-left py-2 text-xs uppercase">Categoría</th>
                    <th className="text-center py-2 text-xs uppercase">Cant.</th>
                    <th className="text-right py-2 text-xs uppercase">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {showInvoiceView.items_grouped.map((group, idx) => (
                    <tr key={idx} className="border-b">
                      <td className="py-3">
                        <span className="font-semibold">{group.category_display}</span>
                        {group.products_detail && (
                          <div className="text-xs text-gray-500 mt-1">
                            {group.products_detail.map((p, i) => (
                              <span key={i}>{p.name} x{p.quantity}{i < group.products_detail.length - 1 ? ', ' : ''}</span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="py-3 text-center">{group.quantity}</td>
                      <td className="py-3 text-right font-semibold">${group.subtotal.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              /* Vista detallada por producto */
              <table className="w-full mb-8">
                <thead>
                  <tr className="border-b-2 border-black">
                    <th className="text-left py-2 text-xs uppercase">Producto</th>
                    <th className="text-center py-2 text-xs uppercase">Cant.</th>
                    <th className="text-right py-2 text-xs uppercase">Precio</th>
                    <th className="text-right py-2 text-xs uppercase">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {(showInvoiceView.items_detail || showInvoiceView.items || []).map((item, idx) => (
                    <tr key={idx} className="border-b">
                      <td className="py-3">{item.product_name}</td>
                      <td className="py-3 text-center">{item.quantity}</td>
                      <td className="py-3 text-right">${item.unit_price?.toLocaleString()}</td>
                      <td className="py-3 text-right font-semibold">${item.subtotal?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            
            {/* Totales - Responsive */}
            <div className="flex justify-end mt-4 sm:mt-0">
              <div className="w-full sm:w-64 space-y-2 p-3 sm:p-0 bg-gray-50 sm:bg-transparent">
                <div className="flex justify-between text-sm sm:text-base">
                  <span>Subtotal</span>
                  <span>${showInvoiceView.subtotal.toLocaleString()}</span>
                </div>
                {showInvoiceView.discount > 0 && (
                  <div className="flex justify-between text-red-600 text-sm sm:text-base">
                    <span>Descuento</span>
                    <span>-${showInvoiceView.discount.toLocaleString()}</span>
                  </div>
                )}
                {showInvoiceView.tax > 0 && (
                  <div className="flex justify-between text-sm sm:text-base">
                    <span>Impuesto</span>
                    <span>${showInvoiceView.tax.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg sm:text-xl font-bold border-t-2 pt-2">
                  <span>TOTAL</span>
                  <span>${showInvoiceView.total.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-green-600 text-sm sm:text-base">
                  <span>Pagado</span>
                  <span>${showInvoiceView.amount_paid.toLocaleString()}</span>
                </div>
                <div className={`flex justify-between font-semibold text-sm sm:text-base ${showInvoiceView.balance_due > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  <span>Saldo</span>
                  <span>${showInvoiceView.balance_due.toLocaleString()}</span>
                </div>
              </div>
            </div>
            
            {showInvoiceView.payments && showInvoiceView.payments.length > 0 && (
              <div className="mt-6 sm:mt-8 p-3 sm:p-4 bg-gray-50">
                <h3 className="text-xs uppercase text-gray-400 mb-3">Historial de Pagos</h3>
                {showInvoiceView.payments.map((p, idx) => (
                  <div key={idx} className="flex justify-between text-sm py-1">
                    <span>{new Date(p.created_at).toLocaleDateString('es-CO')} - {p.payment_method}</span>
                    <span className="font-semibold">${p.amount.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
            
            {showInvoiceView.notes && (
              <p className="mt-4 text-sm italic text-gray-500">Notas: {showInvoiceView.notes}</p>
            )}
            
            {/* Botones de acción */}
            <div className="invoice-actions-container grid grid-cols-2 sm:flex gap-2 sm:gap-4 mt-6 sm:mt-8">
              <button 
                onClick={() => generateInvoicePDF(showInvoiceView)} 
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2.5 sm:py-3 text-xs uppercase tracking-wider flex items-center justify-center gap-1.5 sm:gap-2"
                data-testid="invoice-pdf-btn"
              >
                <Download className="w-4 h-4" />
                <span className="hidden xs:inline">PDF</span>
              </button>
              <button 
                onClick={() => saveInvoiceAsImage(showInvoiceView)} 
                disabled={savingImage}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2.5 sm:py-3 text-xs uppercase tracking-wider flex items-center justify-center gap-1.5 sm:gap-2 disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                <span className="hidden xs:inline">{savingImage ? '...' : 'PNG'}</span>
              </button>
              <button onClick={() => printInvoice(showInvoiceView)} className="flex-1 bg-black text-white py-2.5 sm:py-3 text-xs uppercase tracking-wider flex items-center justify-center gap-1.5 sm:gap-2">
                <Printer className="w-4 h-4" />
                <span className="hidden xs:inline">Imprimir</span>
              </button>
              <button 
                onClick={() => sendInvoiceWhatsApp(showInvoiceView)} 
                className="flex-1 bg-green-500 hover:bg-green-600 text-white py-2.5 sm:py-3 text-xs uppercase tracking-wider flex items-center justify-center gap-1.5 sm:gap-2"
                data-testid="invoice-wa-pdf-btn"
              >
                <Send className="w-4 h-4" />
                <span className="hidden xs:inline">WhatsApp PDF</span>
              </button>
              <button onClick={() => setShowInvoiceView(null)} className="flex-1 border border-gray-300 py-2.5 sm:py-3 text-xs uppercase tracking-wider">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Selector Visual de Productos (estilo Pre-pedido) */}
      {showVisualPicker && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setShowVisualPicker(false)}
        >
          <div
            className="bg-[#0A0A0A] border border-white/10 w-full sm:max-w-6xl max-h-[92vh] sm:max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="visual-picker-modal"
          >
            {/* Header */}
            <div className="px-4 sm:px-6 py-4 border-b border-white/10 flex items-center justify-between gap-3 flex-shrink-0">
              <div>
                <h3 className="text-lg sm:text-xl font-semibold flex items-center gap-2">
                  <Package className="w-5 h-5 text-emerald-400" />
                  Selección Visual de Productos
                </h3>
                <p className="text-[11px] text-white/40 mt-0.5">
                  Click en la imagen para añadir. {invoiceForm.items.filter(i => i.item_type !== 'saldo_anterior').length} producto(s) ya en factura
                </p>
              </div>
              <button
                onClick={() => setShowVisualPicker(false)}
                data-testid="visual-picker-close"
                className="p-2 hover:bg-white/10 rounded transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Filters */}
            <div className="px-4 sm:px-6 py-3 border-b border-white/10 flex flex-col gap-3 flex-shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input
                  type="text"
                  value={visualPickerSearch}
                  onChange={(e) => setVisualPickerSearch(e.target.value)}
                  placeholder="Buscar nombre, marca o SKU..."
                  data-testid="visual-picker-search"
                  className="w-full bg-[#121212] border border-white/10 focus:border-white/30 pl-10 pr-3 py-2.5 text-sm outline-none"
                  autoFocus
                />
              </div>
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => setVisualPickerCategory('all')}
                  data-testid="visual-picker-cat-all"
                  className={`px-2.5 py-1 text-[10px] uppercase tracking-wider rounded transition-colors ${visualPickerCategory === 'all' ? 'bg-white text-black' : 'bg-white/5 text-white/50 hover:bg-white/10'}`}
                >
                  Todas ({products.length})
                </button>
                {[...new Set(products.map(p => p.category).filter(Boolean))].sort().map(cat => {
                  const count = products.filter(p => p.category === cat).length;
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setVisualPickerCategory(cat)}
                      data-testid={`visual-picker-cat-${cat}`}
                      className={`px-2.5 py-1 text-[10px] uppercase tracking-wider rounded transition-colors ${visualPickerCategory === cat ? 'bg-white text-black' : 'bg-white/5 text-white/50 hover:bg-white/10'}`}
                    >
                      {cat.replace(/-/g, ' ')} ({count})
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Products grid */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-4">
              {(() => {
                const filtered = products.filter(p => {
                  if (visualPickerCategory !== 'all' && p.category !== visualPickerCategory) return false;
                  if (!visualPickerSearch) return true;
                  const s = visualPickerSearch.toLowerCase();
                  return (
                    p.name.toLowerCase().includes(s) ||
                    p.brand?.toLowerCase().includes(s) ||
                    p.sku?.toLowerCase().includes(s)
                  );
                });
                if (filtered.length === 0) {
                  return <p className="text-center py-12 text-white/40 text-sm">No se encontraron productos</p>;
                }
                return (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3">
                    {filtered.map(p => {
                      const inv = p.inventory || { china: 0, contenedor: 0, stock: 0 };
                      const stockDisp = p.stock || inv.stock || 0;
                      const inSelection = invoiceForm.items.find(i => i.product_id === p.id);
                      const qtyInFactura = inSelection?.quantity || 0;
                      return (
                        <div
                          key={p.id}
                          data-testid={`visual-picker-product-${p.id}`}
                          className={`relative bg-[#121212] border-2 transition-all ${inSelection ? 'border-emerald-500 shadow-[0_0_0_2px_rgba(16,185,129,0.15)]' : 'border-white/10 hover:border-white/40'}`}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              addItemToInvoice(p);
                              toast.success(`+1 ${p.name.slice(0, 35)}`, { duration: 1500 });
                            }}
                            className="w-full text-left block"
                          >
                            <div className="aspect-square bg-[#0A0A0A] relative overflow-hidden">
                              {p.images?.[0] ? (
                                <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-white/20">
                                  <Package className="w-8 h-8" />
                                </div>
                              )}
                              {qtyInFactura > 0 && (
                                <div className="absolute top-1.5 right-1.5 min-w-[28px] h-7 px-2 bg-emerald-500 rounded-full flex items-center justify-center text-black font-bold text-sm">
                                  {qtyInFactura}
                                </div>
                              )}
                              {stockDisp <= 0 && (
                                <div className="absolute bottom-1.5 left-1.5 bg-orange-500/80 text-white text-[9px] uppercase tracking-wider px-2 py-0.5">
                                  Sin stock
                                </div>
                              )}
                            </div>
                            <div className="px-2 py-2">
                              <p className="text-[10px] uppercase tracking-wider text-white/40 truncate">{p.brand}</p>
                              <p className="text-xs text-white truncate" title={p.name}>{p.name}</p>
                              <div className="flex items-center justify-between mt-1">
                                <span className="text-[11px] font-semibold text-emerald-400">${p.price?.toLocaleString()}</span>
                                <span className={`text-[10px] ${stockDisp > 0 ? 'text-white/40' : 'text-orange-400'}`}>S:{stockDisp}</span>
                              </div>
                            </div>
                          </button>
                          {/* Bottom row: zoom + quick qty if selected */}
                          <div className="flex items-stretch border-t border-white/5">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); if (p.images?.[0]) setZoomImage(p.images[0]); }}
                              className="flex-1 py-1.5 text-[10px] uppercase tracking-wider text-white/50 hover:bg-white/5 transition-colors flex items-center justify-center gap-1"
                              data-testid={`visual-picker-zoom-${p.id}`}
                            >
                              <Search className="w-3 h-3" />
                              Ver
                            </button>
                            {inSelection && (
                              <>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const newItems = [...invoiceForm.items];
                                    const itemIdx = newItems.findIndex(i => i.product_id === p.id);
                                    if (itemIdx >= 0 && newItems[itemIdx].quantity > 1) {
                                      newItems[itemIdx].quantity -= 1;
                                      setInvoiceForm({ ...invoiceForm, items: newItems });
                                    } else if (itemIdx >= 0) {
                                      setInvoiceForm({ ...invoiceForm, items: newItems.filter((_, i) => i !== itemIdx) });
                                    }
                                  }}
                                  className="px-2 text-sm text-red-400 hover:bg-red-500/10 border-l border-white/5"
                                  data-testid={`visual-picker-decrement-${p.id}`}
                                >
                                  −
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); addItemToInvoice(p); }}
                                  className="px-2 text-sm text-emerald-400 hover:bg-emerald-500/10 border-l border-white/5"
                                  data-testid={`visual-picker-increment-${p.id}`}
                                >
                                  +
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {/* Footer */}
            <div className="px-4 sm:px-6 py-3 border-t border-white/10 flex items-center justify-between gap-3 flex-shrink-0 bg-[#070707]">
              <div className="text-xs text-white/50">
                <span className="text-emerald-400 font-semibold">{invoiceForm.items.filter(i => i.item_type !== 'saldo_anterior').reduce((s, i) => s + i.quantity, 0)}</span> unidades · {' '}
                <span className="text-white">${invoiceForm.items.reduce((s, i) => s + i.quantity * i.unit_price, 0).toLocaleString()}</span> total
              </div>
              <button
                onClick={() => setShowVisualPicker(false)}
                data-testid="visual-picker-done"
                className="bg-white text-black hover:bg-gray-200 px-5 py-2 text-xs uppercase tracking-[0.2em] font-semibold"
              >
                Listo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Zoom de Imagen */}
      {zoomImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4"
          onClick={() => setZoomImage(null)}
        >
          <button
            onClick={() => setZoomImage(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white"
          >
            <X className="w-8 h-8" />
          </button>
          <img 
            src={zoomImage} 
            alt="Producto" 
            className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Dialog: Editar Pago */}
      {showEditPaymentDialog && editingPayment && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowEditPaymentDialog(false)}>
          <div className="bg-[#0A0A0A] border border-white/10 p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Edit className="w-5 h-5" />
                Editar Pago
              </h3>
              <button onClick={() => setShowEditPaymentDialog(false)} className="p-1 hover:bg-white/10">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20">
              <p className="text-sm text-white/60">Cliente</p>
              <p className="font-semibold">{editingPayment.client_name}</p>
              <p className="text-xs text-white/40 mt-1">
                {new Date(editingPayment.date).toLocaleDateString('es-CO')}
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs uppercase text-white/60 mb-2">Monto</label>
                <input
                  type="number"
                  min="0"
                  value={paymentEditForm.amount}
                  onChange={(e) => setPaymentEditForm({ ...paymentEditForm, amount: parseFloat(e.target.value) || 0 })}
                  className="w-full bg-[#111] border border-white/10 px-4 py-3 outline-none focus:border-green-500/50 text-lg font-semibold"
                />
              </div>
              
              <div>
                <label className="block text-xs uppercase text-white/60 mb-2">Método de Pago</label>
                <select
                  value={paymentEditForm.payment_method}
                  onChange={(e) => setPaymentEditForm({ ...paymentEditForm, payment_method: e.target.value })}
                  className="w-full bg-[#111] border border-white/10 px-4 py-3 outline-none focus:border-white/30"
                >
                  <option value="efectivo">Efectivo</option>
                  <option value="transferencia">Transferencia</option>
                  <option value="nequi">Nequi</option>
                  <option value="daviplata">Daviplata</option>
                  <option value="tarjeta">Tarjeta</option>
                </select>
              </div>
              
              <div>
                <label className="block text-xs uppercase text-white/60 mb-2">Notas</label>
                <textarea
                  value={paymentEditForm.notes}
                  onChange={(e) => setPaymentEditForm({ ...paymentEditForm, notes: e.target.value })}
                  rows={2}
                  className="w-full bg-[#111] border border-white/10 px-4 py-3 outline-none focus:border-white/30 resize-none"
                  placeholder="Notas adicionales..."
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSavePaymentEdit}
                className="flex-1 bg-green-500 text-white py-3 text-xs uppercase tracking-wider font-semibold hover:bg-green-600 transition-colors"
              >
                Guardar Cambios
              </button>
              <button
                onClick={() => setShowEditPaymentDialog(false)}
                className="flex-1 border border-white/20 py-3 text-xs uppercase tracking-wider hover:bg-white/5 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dialog: Confirmar Eliminación de Pago */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowDeleteConfirm(null)}>
          <div className="bg-[#0A0A0A] border border-red-500/30 p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4 text-red-400">
              <AlertTriangle className="w-6 h-6" />
              <h3 className="text-lg font-semibold">Eliminar Pago</h3>
            </div>
            
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20">
              <p className="text-sm mb-2">¿Estás seguro de eliminar este pago?</p>
              <div className="text-sm text-white/60 space-y-1">
                <p><strong>Cliente:</strong> {showDeleteConfirm.client_name}</p>
                <p><strong>Monto:</strong> ${showDeleteConfirm.amount.toLocaleString()}</p>
                <p><strong>Fecha:</strong> {new Date(showDeleteConfirm.date).toLocaleDateString('es-CO')}</p>
              </div>
              <p className="text-xs text-red-400 mt-3">
                ⚠️ Esta acción revertirá el pago en las facturas y el balance del cliente.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => handleDeletePayment(showDeleteConfirm.id)}
                className="flex-1 bg-red-500 text-white py-3 text-xs uppercase tracking-wider font-semibold hover:bg-red-600 transition-colors"
              >
                Sí, Eliminar
              </button>
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 border border-white/20 py-3 text-xs uppercase tracking-wider hover:bg-white/5 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dialog: Confirmar Anulación/Eliminación de Factura */}
      {showInvoiceDeleteConfirm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowInvoiceDeleteConfirm(null)}>
          <div className={`bg-[#0A0A0A] border p-6 max-w-md w-full ${invoiceDeleteAction === 'delete' ? 'border-red-500/30' : 'border-orange-500/30'}`} onClick={e => e.stopPropagation()}>
            <div className={`flex items-center gap-3 mb-4 ${invoiceDeleteAction === 'delete' ? 'text-red-400' : 'text-orange-400'}`}>
              <AlertTriangle className="w-6 h-6" />
              <h3 className="text-lg font-semibold">
                {invoiceDeleteAction === 'delete' ? 'Eliminar Factura' : 'Anular Factura'}
              </h3>
            </div>
            
            <div className={`mb-6 p-4 border ${invoiceDeleteAction === 'delete' ? 'bg-red-500/10 border-red-500/20' : 'bg-orange-500/10 border-orange-500/20'}`}>
              <p className="text-sm mb-3">
                {invoiceDeleteAction === 'delete' 
                  ? '¿Estás seguro de ELIMINAR definitivamente esta factura?' 
                  : '¿Estás seguro de ANULAR esta factura?'}
              </p>
              <div className="text-sm text-white/60 space-y-1">
                <p><strong>Factura:</strong> #{showInvoiceDeleteConfirm.invoice_number}</p>
                <p><strong>Cliente:</strong> {showInvoiceDeleteConfirm.client_name}</p>
                <p><strong>Total:</strong> ${showInvoiceDeleteConfirm.total?.toLocaleString()}</p>
                <p><strong>Estado actual:</strong> <span className="uppercase">{showInvoiceDeleteConfirm.status}</span></p>
              </div>
              <p className={`text-xs mt-3 ${invoiceDeleteAction === 'delete' ? 'text-red-400' : 'text-orange-400'}`}>
                {invoiceDeleteAction === 'delete' 
                  ? '⚠️ Esta acción es IRREVERSIBLE. La factura será eliminada permanentemente.'
                  : '⚠️ Al anular, el stock de los productos será devuelto al inventario.'}
              </p>
            </div>

            {/* Opción de elegir acción para facturas no anuladas */}
            {showInvoiceDeleteConfirm.status !== 'anulada' && (
              <div className="mb-4">
                <label className="block text-xs uppercase text-white/60 mb-2">Acción a realizar</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setInvoiceDeleteAction('cancel')}
                    className={`p-3 border text-sm text-center transition-all ${
                      invoiceDeleteAction === 'cancel' 
                        ? 'border-orange-500 bg-orange-500/20 text-orange-400' 
                        : 'border-white/10 hover:border-white/30'
                    }`}
                  >
                    <X className="w-4 h-4 mx-auto mb-1" />
                    Anular
                  </button>
                  <button
                    onClick={() => setInvoiceDeleteAction('delete')}
                    className={`p-3 border text-sm text-center transition-all ${
                      invoiceDeleteAction === 'delete' 
                        ? 'border-red-500 bg-red-500/20 text-red-400' 
                        : 'border-white/10 hover:border-white/30'
                    }`}
                  >
                    <Trash2 className="w-4 h-4 mx-auto mb-1" />
                    Eliminar
                  </button>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  if (invoiceDeleteAction === 'delete') {
                    handleDeleteInvoice(showInvoiceDeleteConfirm.id);
                  } else {
                    handleCancelInvoice(showInvoiceDeleteConfirm.id);
                  }
                }}
                className={`flex-1 text-white py-3 text-xs uppercase tracking-wider font-semibold transition-colors ${
                  invoiceDeleteAction === 'delete' 
                    ? 'bg-red-500 hover:bg-red-600' 
                    : 'bg-orange-500 hover:bg-orange-600'
                }`}
              >
                {invoiceDeleteAction === 'delete' ? 'Sí, Eliminar' : 'Sí, Anular'}
              </button>
              <button
                onClick={() => setShowInvoiceDeleteConfirm(null)}
                className="flex-1 border border-white/20 py-3 text-xs uppercase tracking-wider hover:bg-white/5 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Cambio de precio en lote */}
      {showBulkPriceModal && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[65] flex items-center justify-center p-4"
          onClick={() => setShowBulkPriceModal(false)}
          data-testid="bulk-price-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-[#0A0A0A] border border-white/10 w-full max-w-md p-5 sm:p-6"
          >
            <div className="flex items-center gap-2 mb-2">
              <Tag className="w-5 h-5 text-emerald-400" />
              <h3 className="text-lg font-semibold">Cambiar precio en lote</h3>
            </div>
            <p className="text-xs text-white/50 mb-5">
              Aplica el mismo precio unitario a <span className="text-emerald-400 font-semibold">{selectedItemIds.length}</span> producto(s) seleccionado(s).
            </p>
            <label className="block text-[11px] uppercase tracking-wider mb-2 text-white/70">Nuevo precio unitario</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">$</span>
              <input
                type="number"
                value={bulkPriceInput}
                onChange={(e) => setBulkPriceInput(e.target.value)}
                autoFocus
                placeholder="45000"
                data-testid="bulk-price-input"
                className="w-full bg-[#121212] border border-white/10 focus:border-emerald-500 pl-7 pr-3 py-3 text-lg font-mono outline-none"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    document.querySelector('[data-testid="bulk-price-apply"]')?.click();
                  }
                }}
              />
            </div>
            <div className="mt-3 max-h-40 overflow-y-auto bg-[#121212] border border-white/5 p-2">
              <p className="text-[10px] uppercase tracking-wider text-white/40 mb-2">Se aplicará a:</p>
              {invoiceForm.items
                .filter(i => selectedItemIds.includes(i.product_id))
                .map((it, i) => (
                  <div key={i} className="text-xs text-white/70 py-0.5 truncate">• {it.product_name}</div>
                ))}
            </div>
            <div className="flex gap-2 mt-5">
              <button
                type="button"
                onClick={() => setShowBulkPriceModal(false)}
                className="flex-1 border border-white/20 py-2.5 text-xs uppercase tracking-wider hover:bg-white/5"
              >
                Cancelar
              </button>
              <button
                type="button"
                data-testid="bulk-price-apply"
                onClick={() => {
                  const p = parseFloat(bulkPriceInput);
                  if (isNaN(p) || p < 0) {
                    toast.error('Ingresa un precio válido');
                    return;
                  }
                  const newItems = invoiceForm.items.map(it => (
                    selectedItemIds.includes(it.product_id)
                      ? { ...it, unit_price: p, subtotal: it.quantity * p }
                      : it
                  ));
                  setInvoiceForm({ ...invoiceForm, items: newItems });
                  toast.success(`Precio actualizado en ${selectedItemIds.length} producto(s)`);
                  setShowBulkPriceModal(false);
                  setSelectedItemIds([]);
                  setBulkPriceInput('');
                }}
                className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-black py-2.5 text-xs uppercase tracking-wider font-semibold"
              >
                Aplicar precio
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detalle de cliente (compartido con ClientsPanel) */}
      {clientDetailId && (
        <ClientDetailDialog
          clientId={clientDetailId}
          onClose={() => setClientDetailId(null)}
        />
      )}
    </div>
  );
};

export default BillingPanel;
